import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getPrivacyPolicyParagraphs } from "@/lib/privacyPolicyContent";

// Гейт принятия политики конфиденциальности. Один компонент, два варианта:
//
// - "new" — первый вход, до этого пользователь ничего не принимал
//   (privacy_policy_accepted_version == 0). Полноэкранный, без scrim: это
//   шаг онбординга, а не всплывающее окно поверх чего-то.
// - "updated" — пользователь уже пользовался приложением, но политика с тех
//   пор обновилась. Модалка поверх текущего экрана — в отличие от
//   EmailRequiredDialog здесь нет ни крестика, ни «позже»: продолжать,
//   не согласившись, нельзя.
//
// Оба варианта ведут в один и тот же POST /user/privacy-policy/accept —
// разделение чисто визуальное, серверу «новый» и «обновил» неразличимы.
const PrivacyPolicyGate = ({ variant, onAccept }) => {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const paragraphs = getPrivacyPolicyParagraphs(i18n.language);

  // На iOS/в вебвью Telegram однократное касание вне скроллера (после того
  // как список докручен до края) может оставить документ в состоянии, где
  // сам скроллер перестаёт реагировать на свайпы — жест "утекает" в html/body
  // позади гейта. Жёстко запрещаем документу скроллиться, пока гейт открыт:
  // тогда единственный существующий скролл — внутри блока с текстом.
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const handleAccept = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onAccept();
    } catch (e) {
      setError(t("privacy_policy.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const titleKey = variant === "new" ? "privacy_policy.new_title" : "privacy_policy.updated_title";
  const introKey = variant === "new" ? "privacy_policy.new_intro" : "privacy_policy.updated_intro";

  // Разбито на header/scroller/footer намеренно: снаружи должна двигаться
  // только сама политика, а иконка, заголовок и кнопка — стоять на месте.
  // Для этого внешний контейнер (ниже) зафиксирован по высоте, header и
  // footer — shrink-0, а скроллится только средний блок (flex-1 + min-h-0,
  // без чего flex-контейнер не даст ребёнку сжаться меньше контента).
  const header = (
    <div className="shrink-0">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 ring-1 ring-inset ring-primary/40">
        <ShieldCheck className="h-7 w-7 text-link" />
      </div>

      <h1 className="mt-4 text-xl font-semibold text-foreground text-center">
        {t(titleKey)}
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground/80 text-center">
        {t(introKey)}
      </p>
    </div>
  );

  const scroller = (
    <div
      className="mt-4 min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] p-4 text-left"
      style={{ WebkitOverflowScrolling: "touch" }}
      data-testid="privacy-policy-text"
    >
      {paragraphs.map((paragraph, idx) =>
        paragraph && typeof paragraph === "object" && paragraph.heading ? (
          <h2
            key={idx}
            className="text-xs font-semibold uppercase tracking-wide text-foreground [&:not(:first-child)]:mt-4"
          >
            {paragraph.heading}
          </h2>
        ) : (
          <p key={idx} className="text-xs leading-relaxed text-muted-foreground [&:not(:first-child)]:mt-3">
            {paragraph}
          </p>
        )
      )}
    </div>
  );

  const footer = (
    <div className="shrink-0">
      {error ? (
        <p className="mt-3 text-xs text-destructive text-center">{error}</p>
      ) : null}

      <button
        type="button"
        onClick={handleAccept}
        disabled={submitting}
        className="mt-5 w-full rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        data-testid="privacy-policy-accept"
      >
        {submitting ? t("common.loading", { defaultValue: "…" }) : t("privacy_policy.accept_cta")}
      </button>
    </div>
  );

  if (variant === "new") {
    return (
      <div
        className="fixed inset-0 z-[90] flex touch-none flex-col items-center overflow-hidden bg-background px-6"
        style={{
          paddingTop: "calc(24px + var(--lp-top))",
          paddingBottom: "calc(24px + var(--lp-bottom))",
        }}
        data-testid="privacy-policy-gate-new"
      >
        <div className="flex h-full w-full max-w-sm flex-col">
          {header}
          {scroller}
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div
      className="lp-scrim--fixed z-[95] flex touch-none items-center justify-center p-4"
      data-testid="privacy-policy-gate-updated"
    >
      <div className="lp-veil lp-veil--edged rounded-[22px] flex max-h-[85vh] w-full max-w-md flex-col p-6">
        {header}
        {scroller}
        {footer}
      </div>
    </div>
  );
};

export default PrivacyPolicyGate;
